import React, { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import styles from './dropdown-toggle-button.module.scss';
import useOnClickOutside from '../../../hooks/use-on-click-outside.hook';
import { ChevronDown } from '../icons/icons';
import colorStyle from '../../../styles/utils/variables.module.scss';
import IMenuItem from '../../../models/header/IMenuItem';
import { replaceAttrSpaceWithHyphen } from '../../../utils/string-helpers/replace-attr-space-with-hyphen';
import Link from 'next/link';

export type Props = {
  hostname?: string;
  themeCopyColor?: string;
  brandPrimaryColor?: string;
  invertedTheme?: boolean;
  isMobile?: boolean;
  dropdownItems: IMenuItem[];
};

export default function DropdownToggleButton({
  hostname,
  dropdownItems,
  themeCopyColor,
  brandPrimaryColor,
  invertedTheme,
  isMobile,
}: Props) {
  const ref = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const parentItem = dropdownItems?.[0];
  const childItems = parentItem?.children || [];
  const label = parentItem?.text || 'Add Label';

  const toggleButtonStyle: CSSProperties = useMemo(() => {
    return {
      '--dropdown-toggle-color': themeCopyColor,
      '--dropdown-toggle-active-color': brandPrimaryColor,
    } as CSSProperties;
  }, [themeCopyColor, brandPrimaryColor]);

  const toggleDropdown = () => setIsOpen((prev) => !prev);
  const closeDropdown = () => setIsOpen(false);

  useOnClickOutside(ref, closeDropdown);

  // Escape key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeDropdown();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (!parentItem || childItems.length === 0) return null;

  return (
    <div className='position-relative' ref={ref}>
      <button
        className={`${styles['dropdown-toggle-button']} 
      position-relative
      ${isOpen ? styles['dropdown-toggle-button__open'] : ''} 
      ${invertedTheme ? styles['inverted-theme'] : ''} 
      ${isMobile ? styles['mobile'] : ''} `}
        onClick={toggleDropdown}
        type='button'
        title={label}
        style={toggleButtonStyle}
        aria-expanded={isOpen}
        aria-label={`${isOpen ? 'Close' : 'Open'} ${label}`}
      >
        <span className='d-inline fw-bold'>{label}</span>
        <div className={`${styles['dropdown-toggle-chevron']} image-wrapper d-inline`}>
          <ChevronDown fillColor={colorStyle.white} height={18} width={16} />
        </div>
      </button>
      {isOpen && (
        <div
          className={`${styles['dropdown-toggle-menu']} 
          ${isMobile ? styles['mobile'] : ''} 
          ${isOpen ? styles['show'] : ''}`}
          style={toggleButtonStyle}
          role='menu'
        >
          {childItems.map((item: IMenuItem) => (
            <Link
              key={replaceAttrSpaceWithHyphen(item.text)}
              href={item.url}
              className={`${styles['menu-item']} ${styles[hostname && item.url.includes(hostname) ? 'active' : '']}`}
              target={item.openNewTab ? '_blank' : '_self'}
              role='menuitem'
            >
              {item.text}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
